import React, { useCallback, useEffect, useMemo, useState } from "react";
import PhotoPreview from "../trips/detail/sections/PhotoPreview";
import { Modal } from "../components/Modal";
import styles from "./BillingStopCards.module.scss";

// Stop expense keys that map to a billing cost field (detention/lumper via
// hidden model inputs, the rest via .extra-cost-amount). Used to zero the ones
// no longer selected when recomputing totals.
const STOP_COST_TYPES = [
  "detention",
  "lumper",
  "driver_assist",
  "scales",
  "parking",
];

// Card data is provided by the server-rendered billing form (hidden JSON field).
function readCards() {
  const raw = document.getElementById("id_stop_billing_data")?.value;
  if (!raw) return [];
  try {
    let parsed = JSON.parse(raw);
    if (typeof parsed === "string") parsed = JSON.parse(parsed);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

// Selection keys are per tile ("stopId:fileId" / "stopId:costType") so the same
// File/cost shown on two stops toggles independently. Photo keys are persisted
// verbatim (Billing.stop_photo_keys), so edit restores the exact tiles picked.
function tileKey(stopId, fileId) {
  return `${stopId}:${fileId}`;
}

function costKey(stopId, costType) {
  return `${stopId}:${costType}`;
}

function writeSelected(keys) {
  const hidden = document.getElementById("id_stop_photo_keys");
  if (hidden) hidden.value = JSON.stringify([...keys]);
}

// Per-tile photo keys the form pre-fills on edit (and round-trips on a POST
// re-render).
function readSavedPhotoKeys() {
  const raw = document.getElementById("id_stop_photo_keys")?.value;
  if (!raw) return [];
  try {
    let parsed = JSON.parse(raw);
    if (typeof parsed === "string") parsed = JSON.parse(parsed);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function cardHasPhotos(card) {
  return (
    (card.proof_of_lumper_photos || []).length > 0 ||
    (card.payment_photos || []).length > 0
  );
}

// Selected cost entries ("stopId:costType") round-trip through a hidden field so
// a validation re-render (POST) restores them without relying on the draft.
function readCostKeys() {
  const raw = document.getElementById("id_stop_cost_keys")?.value;
  if (!raw) return [];
  try {
    let parsed = JSON.parse(raw);
    if (typeof parsed === "string") parsed = JSON.parse(parsed);
    return Array.isArray(parsed) ? parsed : [];
  } catch (e) {
    return [];
  }
}

function writeCostKeys(keys) {
  const hidden = document.getElementById("id_stop_cost_keys");
  if (hidden) hidden.value = JSON.stringify([...keys]);
}

// Sum the selected stop entries per cost type and push each absolute total into
// the vanilla form. Absolute (not additive) so re-applying the restored draft
// on reload can never double-count.
function recomputeCosts(cards, addedKeys) {
  const totals = {};
  cards.forEach((card) => {
    [card.lumper, ...(card.other_expenses || [])]
      .filter(Boolean)
      .forEach((entry) => {
        if (addedKeys.has(costKey(card.stop_id, entry.cost_type))) {
          totals[entry.cost_type] =
            (totals[entry.cost_type] || 0) + (parseFloat(entry.amount) || 0);
        }
      });
  });
  STOP_COST_TYPES.forEach((type) => {
    window.billingForm?.setCost(type, totals[type] || 0);
  });
}

// Draft persistence: the form is unsaved while creating, so keep the biller's
// added costs + selected photos in localStorage (per order) and restore them on
// reload — otherwise a refresh silently drops the work and re-offers "Add".
function draftKey(orderId) {
  return `billing-stop-cards:order:${orderId}`;
}

function loadDraft(orderId) {
  if (!orderId) return { costs: [], photos: [] };
  try {
    const data =
      JSON.parse(window.localStorage.getItem(draftKey(orderId))) || {};
    return {
      costs: Array.isArray(data.costs) ? data.costs : [],
      photos: Array.isArray(data.photos) ? data.photos : [],
    };
  } catch (e) {
    return { costs: [], photos: [] };
  }
}

function saveDraft(orderId, costKeys, photoKeys) {
  if (!orderId) return;
  try {
    window.localStorage.setItem(
      draftKey(orderId),
      JSON.stringify({ costs: [...costKeys], photos: [...photoKeys] }),
    );
  } catch (e) {
    // localStorage unavailable: degrade to in-session state only.
  }
}

// QB-integrated: clicking Add must map the cost to a QuickBooks item. This modal
// makes the biller pick that item before the line is created.
function QbItemModal({ isOpen, label, onClose, onConfirm }) {
  const [items, setItems] = useState([]);
  const [selected, setSelected] = useState("");

  useEffect(() => {
    if (!isOpen) return undefined;
    setSelected("");
    let active = true;
    window.billingForm?.getQbServices?.().then((list) => {
      if (active) setItems(Array.isArray(list) ? list : []);
    });
    return () => {
      active = false;
    };
  }, [isOpen]);

  const confirm = () => {
    const item = items.find((it) => String(it.value) === String(selected));
    if (item) onConfirm(item);
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={gettext("Select QuickBooks item")}
      size="sm"
    >
      <div className={styles.qbModal}>
        <p className={styles.qbModalCost}>{label}</p>
        <select
          className={styles.qbModalSelect}
          value={selected}
          onChange={(e) => setSelected(e.target.value)}
        >
          <option value="">{gettext("Select an item…")}</option>
          {items.map((it) => (
            <option key={it.value} value={it.value}>
              {it.label}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={!selected}
          onClick={confirm}
        >
          {gettext("Add")}
        </button>
      </div>
    </Modal>
  );
}

function CostRow({ entry, qbMode, added, onToggle }) {
  const [qbAdded, setQbAdded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const typeName =
    entry.cost_type.charAt(0).toUpperCase() + entry.cost_type.slice(1);
  const label = `${typeName}: ${entry.amount}`;

  // QB mode: Add opens the item picker; on confirm the cost becomes a QuickBooks
  // line item. Lock after adding (removed via that row's own clear button).
  if (qbMode) {
    return (
      <div className={styles.costRow}>
        <span className={styles.costLabel}>{label}</span>
        <button
          type="button"
          className="btn btn-secondary"
          disabled={qbAdded}
          onClick={() => setModalOpen(true)}
        >
          {qbAdded ? gettext("Added") : gettext("Add")}
        </button>
        <QbItemModal
          isOpen={modalOpen}
          label={label}
          onClose={() => setModalOpen(false)}
          onConfirm={(item) => {
            window.billingForm?.addCost(
              entry.cost_type,
              entry.amount,
              item,
              () => setQbAdded(false),
            );
            setQbAdded(true);
            setModalOpen(false);
          }}
        />
      </div>
    );
  }

  return (
    <div className={styles.costRow}>
      <span className={styles.costLabel}>{label}</span>
      <button type="button" className="btn btn-secondary" onClick={onToggle}>
        {added ? gettext("Remove") : gettext("Add")}
      </button>
    </div>
  );
}

function PhotoGroup({ label, photos, stopId, selected, onToggle }) {
  if (!photos || photos.length === 0) return null;
  return (
    <div className={styles.photoGroup}>
      <div className={styles.photoLabel}>{label}</div>
      <div className={styles.photoList}>
        {photos.map((photo) => {
          const key = tileKey(stopId, photo.id);
          const attached = selected.has(key);
          return (
            <div
              key={key}
              className={`${styles.photoItem} ${attached ? styles.photoItemAttached : ""}`}
            >
              <PhotoPreview
                src={photo.url}
                name={photo.name}
                mimeType={photo.mime_type}
                isImage={photo.is_image}
                thumbClassName={styles.photoThumb}
              />
              <span className={styles.photoName} title={photo.name}>
                {photo.name || gettext("Photo")}
              </span>
              {attached ? (
                <button
                  type="button"
                  className={styles.photoRemove}
                  onClick={() => onToggle(key)}
                  title={gettext("Remove from invoice")}
                  aria-label={gettext("Remove from invoice")}
                >
                  <i className="fa-solid fa-xmark" aria-hidden="true" />
                </button>
              ) : (
                <button
                  type="button"
                  className={styles.photoAdd}
                  onClick={() => onToggle(key)}
                >
                  <i className="fa-solid fa-plus" aria-hidden="true" />
                  {gettext("Add")}
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

function StopCard({
  card,
  editMode,
  qbMode,
  addedCosts,
  onToggleCost,
  selectedPhotos,
  onTogglePhoto,
}) {
  const [collapsed, setCollapsed] = useState(false);

  // On edit the per-stop cost selection can't be reconstructed (only totals are
  // stored), so costs are read above in the disabled fields and the cards just
  // manage photos. Skip stops that have no photos to manage.
  if (editMode && !cardHasPhotos(card)) return null;

  const renderCost = (entry) =>
    editMode ? null : (
      <CostRow
        key={entry.cost_type}
        entry={entry}
        qbMode={qbMode}
        added={addedCosts.has(costKey(card.stop_id, entry.cost_type))}
        onToggle={() => onToggleCost(costKey(card.stop_id, entry.cost_type))}
      />
    );

  return (
    <div className={styles.card}>
      <button
        type="button"
        className={styles.cardHead}
        onClick={() => setCollapsed((v) => !v)}
        aria-expanded={!collapsed}
      >
        <span className={styles.cardTitle}>
          {card.label}
          {card.shared_order_count > 1 && (
            <span className={styles.shared}>
              {` (${gettext("shared across")} ${card.shared_order_count} ${gettext(
                "orders",
              )})`}
            </span>
          )}
        </span>
        <i
          className={`fa-solid fa-chevron-down ${styles.chevron} ${collapsed ? styles.chevronCollapsed : ""}`}
          aria-hidden="true"
        />
      </button>
      {!collapsed && (
        <div className={styles.cardBody}>
          {card.lumper && renderCost(card.lumper)}
          <PhotoGroup
            label={gettext("Lumper photos")}
            photos={card.proof_of_lumper_photos}
            stopId={card.stop_id}
            selected={selectedPhotos}
            onToggle={onTogglePhoto}
          />
          {(card.other_expenses || []).map(renderCost)}
          <PhotoGroup
            label={gettext("Other photos")}
            photos={card.payment_photos}
            stopId={card.stop_id}
            selected={selectedPhotos}
            onToggle={onTogglePhoto}
          />
        </div>
      )}
    </div>
  );
}

export default function BillingStopCards({ orderId, editMode = false }) {
  const cards = useMemo(readCards, []);
  const qbMode = useMemo(
    () => !!document.getElementById("qb-services-container"),
    [],
  );
  // Create: restore the unsaved draft. Edit: the saved billing is the source of
  // truth — pre-select its attached photos and leave the (saved) cost fields be.
  const draft = useMemo(
    () => (editMode ? { costs: [], photos: [] } : loadDraft(orderId)),
    [orderId, editMode],
  );
  // Field values (round-tripped through the POST, or pre-filled on edit) take
  // priority; the localStorage draft only covers a plain GET refresh of the
  // unsaved create form.
  const [selectedPhotos, setSelectedPhotos] = useState(() => {
    const fromField = readSavedPhotoKeys();
    if (editMode || fromField.length) return new Set(fromField);
    return new Set(draft.photos);
  });
  // QB costs become line items and edit can't reconstruct attribution, so only
  // restore added costs for a non-QB create.
  const [addedCosts, setAddedCosts] = useState(() => {
    if (editMode || qbMode) return new Set();
    const fromField = readCostKeys();
    return new Set(fromField.length ? fromField : draft.costs);
  });

  // Apply the island's state to the vanilla form (and persist the draft on
  // create) — on first mount (restoring a reload) and on every toggle. On edit
  // the cost fields keep their saved values, so don't recompute them.
  useEffect(() => {
    writeSelected(selectedPhotos);
    if (!editMode) {
      writeCostKeys(addedCosts);
      if (!qbMode) recomputeCosts(cards, addedCosts);
      saveDraft(orderId, qbMode ? new Set() : addedCosts, selectedPhotos);
    }
  }, [cards, qbMode, editMode, orderId, addedCosts, selectedPhotos]);

  // Clear the draft once the form is submitted so deleting the billing and
  // creating it again starts from scratch (a validation re-render restores from
  // the round-tripped fields, then re-saves the draft on mount).
  useEffect(() => {
    if (editMode || !orderId) return undefined;
    const form = document
      .getElementById("id_stop_billing_data")
      ?.closest("form");
    if (!form) return undefined;
    const clear = () => {
      try {
        window.localStorage.removeItem(draftKey(orderId));
      } catch (e) {
        // ignore
      }
    };
    form.addEventListener("submit", clear);
    return () => form.removeEventListener("submit", clear);
  }, [editMode, orderId]);

  const toggleCost = useCallback((key) => {
    setAddedCosts((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const togglePhoto = useCallback((key) => {
    setSelectedPhotos((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  if (cards.length === 0) return null;

  return (
    <div className={styles.cards}>
      {cards.map((card) => (
        <StopCard
          key={card.stop_id}
          card={card}
          editMode={editMode}
          qbMode={qbMode}
          addedCosts={addedCosts}
          onToggleCost={toggleCost}
          selectedPhotos={selectedPhotos}
          onTogglePhoto={togglePhoto}
        />
      ))}
    </div>
  );
}
